import LazyVideo from "@/components/LazyVideo";
import React, { useMemo, useRef, useState } from 'react';
import { Seo } from '../components/Seo';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Leaf,
  ShieldCheck,
  HeartHandshake,
  Loader2,
  ClipboardList,
  MessageCircle,
  FlaskConical,
  HelpCircle,
  Truck,
  Plus,
  Minus,
} from 'lucide-react';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  type Answers,
  CATEGORIES,
  getQuestionSection,
  getVisibleQuestions,
  type RxQuestion as Question,
} from '@/types/personalRx';
import { useContent } from '@/context/ContentContext';


const contactSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: 'נא להזין שם מלא' })
    .max(100, { message: 'שם ארוך מדי' }),
  phone: z
    .string()
    .trim()
    .min(7, { message: 'נא להזין מספר טלפון תקין' })
    .max(30, { message: 'מספר טלפון ארוך מדי' })
    .regex(/^[0-9+\-\s()]+$/, { message: 'מספר טלפון לא תקין' }),
  email: z
    .string()
    .trim()
    .email({ message: 'נא להזין כתובת אימייל תקינה' })
    .max(255),
  note: z.string().trim().max(500).optional(),
});

type ContactValues = z.infer<typeof contactSchema>;

const isAnswered = (q: Question, a: Answers) => {
  const v = a[q.id];
  if (q.type === 'multi') return Array.isArray(v) && v.length > 0;
  if (q.type === 'text') return typeof v === 'string' && v.trim().length > 0;
  return typeof v === 'string' && v.length > 0;
};

// Group consecutive yes/no (כן/לא) questions in the same section into
// batches of up to 3, so users can answer them on a single screen.
type Step =
  | { kind: 'single'; q: Question; section: string | null }
  | { kind: 'group'; questions: Question[]; section: string | null };

const isYesNo = (q: Question) =>
  q.type === 'single' &&
  !!q.options &&
  q.options.length === 2 &&
  q.options.includes('כן') &&
  q.options.includes('לא');

const buildSteps = (
  visible: Question[],
  sectionOf: (q: Question) => string | null,
): Step[] => {
  const steps: Step[] = [];
  let buf: Question[] = [];
  let bufSection: string | null = null;
  const flush = () => {
    if (buf.length === 0) return;
    if (buf.length === 1)
      steps.push({ kind: 'single', q: buf[0], section: bufSection });
    else steps.push({ kind: 'group', questions: buf, section: bufSection });
    buf = [];
    bufSection = null;
  };
  for (const q of visible) {
    if (isYesNo(q)) {
      const sec = sectionOf(q);
      if (buf.length === 0) {
        bufSection = sec;
        buf.push(q);
      } else if (sec === bufSection && buf.length < 3) {
        buf.push(q);
      } else {
        flush();
        bufSection = sec;
        buf.push(q);
      }
    } else {
      flush();
      steps.push({ kind: 'single', q, section: sectionOf(q) });
    }
  }
  flush();
  return steps;
};


const SingleOption: React.FC<{
  value?: string;
  options: string[];
  onChange: (v: string) => void;
}> = ({ value, options, onChange }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    {options.map((opt) => {
      const selected = value === opt;
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`group relative text-right px-5 py-4 border transition-all duration-300 ${
            selected
              ? 'border-[#fbbf25] bg-[#fbbf25]/10 text-[#412018] shadow-[0_8px_30px_-12px_rgba(251,191,37,0.45)]'
              : 'border-[#412018]/15 bg-brand-sand hover:border-[#67392b]/40 hover:bg-brand-bark/5 text-[#412018]/80'
          }`}
        >
          <span className="font-light leading-snug">{opt}</span>
          {selected && (
            <Check
              size={16}
              className="absolute top-1/2 -translate-y-1/2 left-4 text-[#67392b]"
            />
          )}
        </button>
      );
    })}
  </div>
);

const MultiOption: React.FC<{
  value?: string[];
  options: string[];
  onChange: (v: string[]) => void;
}> = ({ value = [], options, onChange }) => {
  const toggle = (opt: string) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {options.map((opt) => {
        const selected = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`relative text-right px-5 py-4 border transition-all duration-300 ${
              selected
                ? 'border-[#fbbf25] bg-[#fbbf25]/10 text-[#412018]'
                : 'border-[#412018]/15 bg-brand-sand hover:border-[#67392b]/40 hover:bg-brand-bark/5 text-[#412018]/80'
            }`}
          >
            <span className="font-light">{opt}</span>
            {selected && (
              <Check
                size={16}
                className="absolute top-1/2 -translate-y-1/2 left-4 text-[#67392b]"
              />
            )}
          </button>
        );
      })}
    </div>
  );
};

const FAQItem: React.FC<{ q: string; a: string; index: number }> = ({ q, a, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-5% 0px' }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      className="border-b border-[#412018]/15 last:border-b-0"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-6 py-7 md:py-8 text-right group/q"
      >
        <span className="font-serif text-[#412018] text-[1.8rem] md:text-[2.2rem] lg:text-[2.6rem] leading-[1.15] flex-1 group-hover/q:text-[#67392b] transition-colors">
          {q}
        </span>
        <span className="w-10 h-10 rounded-full border border-[#412018]/25 flex items-center justify-center text-[#67392b] shrink-0 group-hover/q:bg-[#67392b] group-hover/q:text-[#EAE6D7] transition-colors duration-300">
          {open ? <Minus size={16} strokeWidth={1.6} /> : <Plus size={16} strokeWidth={1.6} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="text-[#412018]/75 font-light text-[15px] md:text-[16px] leading-[1.85] pb-8 max-w-[60ch]">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const PersonalRx: React.FC = () => {
  const { toast } = useToast();
  const wizardRef = useRef<HTMLElement>(null);
  const scrollToWizard = () => {
    wizardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const [stepIndex, setStepIndex] = useState(0); // 0 = first question, length = contact, length+1 = success
  const [answers, setAnswers] = useState<Answers>({});
  const [contact, setContact] = useState<ContactValues>({
    name: '',
    phone: '',
    email: '',
    note: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactValues, string>>>({});
  const [submitting, setSubmitting] = useState(false);

  const { personalRxQuestions: dbQuestions } = useContent();
  const allQuestions = useMemo(() => dbQuestions ?? [], [dbQuestions]);
  const visible = useMemo(
    () => getVisibleQuestions(allQuestions, answers),
    [allQuestions, answers],
  );
  const steps = useMemo(
    () => buildSteps(visible, getQuestionSection),
    [visible],
  );
  const totalSteps = steps.length;
  const onContact = stepIndex === totalSteps;
  const onSuccess = stepIndex === totalSteps + 1;
  const currentStep = !onContact && !onSuccess ? steps[stepIndex] : null;
  // Back-compat alias used elsewhere in the file (e.g. submit summary)
  const total = totalSteps;

  // Section progress (how many steps belong to the current section / where we are within them)
  const sectionInfo = useMemo(() => {
    if (!currentStep || !currentStep.section) return null;
    const same = steps.filter((s) => s.section === currentStep.section);
    const idx = same.findIndex((s) => s === currentStep);
    return { name: currentStep.section, idx, total: same.length };
  }, [currentStep, steps]);

  // Ordered list of categories the user picked (in canonical order),
  // plus a virtual "general" stage at the end for the shared questions.
  const categoryProgress = useMemo(() => {
    const selected = Array.isArray(answers.category)
      ? (answers.category as string[])
      : typeof answers.category === 'string'
        ? [answers.category]
        : [];
    const ordered = [CATEGORIES.HAIR, CATEGORIES.FACE, CATEGORIES.NAILS].filter(
      (c) => selected.includes(c),
    );
    const generalLabel = 'שאלות כלליות';
    const stages: string[] = [...ordered, generalLabel];
    const currentName = currentStep?.section ?? null;
    const currentIdx = currentName ? stages.indexOf(currentName) : -1;
    return { stages, currentIdx, total: stages.length };
  }, [answers.category, currentStep]);

  const progress = onSuccess
    ? 100
    : Math.round(((stepIndex + (onContact ? 1 : 0.5)) / (totalSteps + 1)) * 100);

  const setAnswer = (id: string, v: string | string[]) =>
    setAnswers((prev) => {
      const next = { ...prev, [id]: v };
      // When the category or main problem changes, drop branch-specific answers
      // that no longer apply, so the wizard recalculates a clean question set.
      if (id === 'category' || id === 'main_problem' || id === 'face_problem' || id === 'nails_problem') {
        const nowVisible = new Set(getVisibleQuestions(allQuestions, next).map((q) => q.id));
        for (const key of Object.keys(next)) {
          if (!nowVisible.has(key) && key !== id) delete next[key];
        }
      }
      return next;
    });

  const stepUnanswered = (step: Step | null): Question | null => {
    if (!step) return null;
    const qs = step.kind === 'single' ? [step.q] : step.questions;
    for (const q of qs) if (q.required && !isAnswered(q, answers)) return q;
    return null;
  };

  const goNext = () => {
    const missing = stepUnanswered(currentStep);
    if (missing) {
      toast({ title: 'נא לענות על כל השאלות בשלב', variant: 'destructive' });
      return;
    }
    setStepIndex((i) => Math.min(i + 1, totalSteps));
  };
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const handleSubmit = async () => {
    const parsed = contactSchema.safeParse(contact);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof ContactValues, string>> = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as keyof ContactValues;
        if (!fieldErrors[k]) fieldErrors[k] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    // Build human-readable summary for the owner
    const summaryLines: string[] = [];
    for (const q of visible) {
      const v = answers[q.id];
      if (v === undefined || (Array.isArray(v) && v.length === 0)) continue;
      summaryLines.push(`• ${q.label}\n   ${Array.isArray(v) ? v.join(', ') : v}`);
    }
    if (parsed.data.note) summaryLines.push(`\nהערות: ${parsed.data.note}`);
    const summary = summaryLines.join('\n');

    const { error } = await supabase.from('personal_rx_submissions').insert({
      name: parsed.data.name,
      phone: parsed.data.phone,
      email: parsed.data.email,
      answers: { ...answers, _note: parsed.data.note ?? '' },
      summary,
    });

    setSubmitting(false);
    if (error) {
      toast({
        title: 'שליחת השאלון נכשלה',
        description: 'נסי שוב בעוד רגע או צרי קשר טלפונית',
        variant: 'destructive',
      });
      return;
    }
    setStepIndex(total + 1);
  };

  const reset = () => {
    setAnswers({});
    setContact({ name: '', phone: '', email: '', note: '' });
    setErrors({});
    setStepIndex(0);
  };

  return (
    <>
      <Seo
        title="רוקחות אישית | Personal Rx — RIHAM"
        description="ייעוץ ורוקחות צמחים אישית של רהאם מנסור — תכשירים מותאמים אישית לעור, שיער ובריאות לפי שאלון מקצועי."
        path="/personal-rx"
      />
    <div
      className="bg-brand-cream min-h-[100dvh] pt-32 pb-24 text-[#412018] relative"
      dir="rtl"
    >
      <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply" />

      {/* HERO */}
      <section className="relative w-full min-h-[560px] h-[78vh] max-h-[820px] overflow-hidden -mt-32 mb-12 md:mb-20">
        <LazyVideo
          src="https://cdn.shopify.com/videos/c/o/v/0971090ff8e54fdcaff90d1707f5caa0.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
         aria-hidden="true" />
        <div className="absolute inset-0 bg-[hsl(47_31%_88%/0.6)]" />
        <div
          className="absolute -bottom-px left-0 right-0 h-[60%] pointer-events-none"
          style={{
            background:
              'linear-gradient(to top, hsl(47 31% 88%) 0%, hsl(47 31% 88% / 0.9) 18%, hsl(47 31% 88% / 0.7) 38%, hsl(47 31% 88% / 0.35) 62%, hsl(47 31% 88% / 0) 100%)',
          }}
        />
        <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply" />

        <div className="relative h-full flex flex-col items-center justify-center text-center px-6 pt-28 pb-24 md:pb-32">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-6"
          >
            הבוטיק של רהאם מנסור
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-[#412018] leading-[0.9] tracking-wide text-[3rem] sm:text-7xl md:text-8xl lg:text-[8.5rem] xl:text-[10rem]"
          >
            רוקחות <span className="text-[#67392b] italic">אישית</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-6 max-w-2xl text-[#412018]/75 text-base md:text-lg font-light leading-relaxed"
          >
            עוני על כמה שאלות קצרות — רהאם תיצור איתך קשר ותתאים לך תרכובת מדויקת
            לפי הצרכים, הקרקפת והמטרות שלך.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-10 flex items-center gap-6 text-[11px] uppercase tracking-[0.3em] text-[#67392b]"
          >
            <span className="flex items-center gap-2">
              <Leaf size={14} /> טבעי
            </span>
            <span className="w-px h-4 bg-[#67392b]/30" />
            <span className="flex items-center gap-2">
              <HeartHandshake size={14} /> מותאם אישית
            </span>
            <span className="hidden sm:inline w-px h-4 bg-[#67392b]/30" />
            <span className="hidden sm:flex items-center gap-2">
              <ShieldCheck size={14} /> דיסקרטי
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
          >
            <button
              onClick={scrollToWizard}
              className="group relative overflow-hidden bg-[#412018] text-[#fbbf25] px-8 py-4 text-[11px] font-bold uppercase tracking-[0.28em] transition-colors duration-500"
            >
              <span className="absolute inset-0 bg-[#fbbf25] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
              <span className="relative flex items-center gap-3 group-hover:text-[#412018] transition-colors duration-500">
                התחילי את השאלון
                <ArrowLeft size={14} />
              </span>
            </button>
            <Link
              to="/shop"
              className="px-8 py-4 text-[11px] font-bold uppercase tracking-[0.28em] border border-[#412018]/25 text-[#412018]/80 hover:bg-[#412018] hover:text-[#fbbf25] transition-colors"
            >
              למוצרים המוכנים
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ESSENCE + DOMAINS — merged editorial section */}
      <section className="relative overflow-hidden py-16 md:py-32" dir="rtl">
        <div className="container mx-auto px-5 md:px-6 max-w-[1200px] relative">
          {/* Editorial intro */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14 md:mb-20">
            <div className="lg:col-span-7">
              <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-5 md:mb-7">
                Essence · Domains
              </p>
              <h2 className="font-serif leading-[0.92] tracking-wide text-[3.4rem] sm:text-6xl md:text-7xl lg:text-[6.5rem]">
                <span className="text-[#412018]">תרכובת </span>
                <span className="text-[#67392b] italic">שמכירה אותך</span>
              </h2>
              <div className="w-12 h-px bg-[#67392b] mt-8 mb-8" />
            </div>
            <div className="lg:col-span-5">
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-5% 0px' }}
                transition={{ duration: 0.6 }}
                className="text-[#412018]/80 font-light text-[15.5px] md:text-[17px] leading-[1.85]"
              >
                רוקחות אישית פירושה שרהאם מרכיבה תרכובת ייעודית לך — לפי הבעיה, סוג העור או הקרקפת, והמטרות.
                בלי קרמים גנריים, בלי ניחושים. שלושה תחומי טיפול, כל אחד עם פרוטוקול שאלון משלו.
              </motion.p>
            </div>
          </div>

          {/* Domains cards — light editorial */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                en: 'Hair & Scalp',
                num: '01',
                label: 'שיער וקרקפת',
                body: 'נשירה, קשקשים, יובש, דלילות, חידוש וברק. תרכובות שמטפלות בקרקפת ומחזירות חיים לשיער.',
              },
              {
                en: 'Facial Skin',
                num: '02',
                label: 'עור הפנים',
                body: 'אנטי אייג׳ינג, פיגמנטציה, אקנה, רגישות, סבוריאה ושיקום העור — עם רכיבים פעילים בדיוק הנכון.',
              },
              {
                en: 'Nails & Hands',
                num: '03',
                label: 'ציפורניים וידיים',
                body: 'פטרת, אקזמה, יובש, סדקים וחיזוק ציפורניים — פתרונות מבוססי שמנים וצמחי מרפא.',
              },
            ].map((sec, idx) => (
              <motion.div
                key={sec.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-5% 0px' }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className="group relative bg-brand-sand text-brand-bark p-7 md:p-10 flex flex-col gap-4 md:gap-5 min-h-[260px] md:min-h-[320px] overflow-hidden border border-brand-bark/10 shadow-brand"
              >
                <span
                  className="absolute -top-5 left-5 font-serif italic text-[7rem] md:text-[9rem] leading-none text-brand-bark/6 select-none pointer-events-none"
                  aria-hidden
                >
                  {sec.num}
                </span>
                <span className="relative text-[10px] uppercase tracking-[0.35em] text-brand-terracotta font-bold">
                  {sec.en}
                </span>
                <h3 className="relative font-serif leading-[1] tracking-wide text-brand-bark text-[2.2rem] md:text-[2.6rem] lg:text-[3rem]">
                  {sec.label}
                </h3>
                <div className="relative w-10 h-px bg-brand-gold/80" />
                <p className="relative text-brand-bark/75 font-light leading-relaxed text-[14.5px] md:text-[15px] mt-auto">
                  {sec.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — timeline */}
      <section className="relative py-16 md:py-32" dir="rtl">
        <div className="container mx-auto px-5 md:px-6 max-w-[1200px] relative">
          <div className="text-center max-w-[820px] mx-auto mb-12 md:mb-20">
            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-5 md:mb-7">
              The Process
            </p>
            <h2 className="font-serif leading-[0.92] tracking-wide text-[3.2rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] mb-6 md:mb-8">
              <span className="text-[#412018]">איך זה </span>
              <span className="text-[#67392b] italic">עובד</span>
            </h2>
            <div className="w-12 h-px bg-[#67392b] mx-auto mb-6 md:mb-8" />
            <p className="text-[#412018]/70 font-light text-[15px] md:text-lg leading-relaxed">
              חמישה שלבים פשוטים — מהשאלון ועד תרכובת אישית מוכנה.
            </p>
          </div>

          {(() => {
            const steps = [
              { icon: ClipboardList, step: '01', title: 'בוחרים תחום', desc: 'שיער, פנים או ציפורניים — לפי הצורך שלך.' },
              { icon: HelpCircle, step: '02', title: 'עונים על שאלות', desc: '2-3 דקות שאלון מותאם אישית למצב שלך.' },
              { icon: MessageCircle, step: '03', title: 'רהאם יוצרת קשר', desc: 'מציגה המלצה, הסבר על הרכיבים ותשובות לשאלות.' },
              { icon: FlaskConical, step: '04', title: 'מרכיבה במיוחד', desc: 'בונה תרכובת ייעודית לפי התשובות שלך.' },
              { icon: Truck, step: '05', title: 'משלוח אליך', desc: 'מקבלים את התרכובת ישירות לבית או לסניף.' },
            ];
            return (
              <>
                {/* DESKTOP — horizontal */}
                <div className="hidden sm:block relative max-w-[1100px] mx-auto">
                  <div className="hidden lg:block absolute top-[44px] right-[10%] left-[10%] h-px bg-gradient-to-l from-transparent via-brand-terracotta/35 to-transparent" />
                  <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-5 relative">
                    {steps.map((s, i) => (
                      <motion.div
                        key={s.step}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-5% 0px' }}
                        transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                        className="relative flex flex-col items-center text-center group"
                      >
                        <div className="relative mb-7">
                          <div className="w-[88px] h-[88px] rounded-full bg-brand-sand border border-brand-bark/15 flex items-center justify-center shadow-brand group-hover:bg-brand-bark transition-colors duration-500">
                            <s.icon size={32} strokeWidth={1.4} className="text-brand-bark transition-colors duration-500 group-hover:text-brand-gold" />
                          </div>
                          <span className="absolute -top-2 -left-2 bg-brand-gold text-brand-bark text-[10px] tracking-[0.2em] font-bold px-2.5 py-1 rounded-full">
                            {s.step}
                          </span>
                        </div>
                        <h3 className="font-serif text-brand-bark text-[1.7rem] md:text-[1.9rem] leading-[1.1] mb-3">{s.title}</h3>
                        <p className="text-brand-bark/70 font-light text-[14.5px] md:text-[15px] leading-[1.7] max-w-[260px]">{s.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* MOBILE — vertical timeline */}
                <div className="sm:hidden relative max-w-[440px] mx-auto pr-2">
                  <div
                    className="absolute top-3 bottom-3 right-[34px] w-px"
                    style={{
                      backgroundImage: 'linear-gradient(to bottom, hsl(var(--brand-terracotta)) 0 6px, transparent 6px 14px)',
                      backgroundSize: '1px 14px',
                      backgroundRepeat: 'repeat-y',
                      opacity: 0.35,
                    }}
                  />
                  <div className="space-y-6">
                    {steps.map((s, i) => (
                      <motion.div
                        key={s.step}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-5% 0px' }}
                        transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                        className="relative flex items-stretch gap-4"
                      >
                        <div className="relative shrink-0 z-10">
                          <div className="w-[68px] h-[68px] rounded-full bg-brand-sand border border-brand-bark/15 flex items-center justify-center shadow-brand-sm ring-4 ring-brand-cream">
                            <s.icon size={26} strokeWidth={1.4} className="text-brand-bark" />
                          </div>
                        </div>
                        <div className="relative flex-1 px-1 py-2 overflow-hidden">
                          <span
                            className="absolute -top-4 left-0 font-serif italic text-[6.5rem] leading-none text-brand-terracotta/20 select-none pointer-events-none"
                            aria-hidden
                          >
                            {s.step}
                          </span>
                          <div className="relative">
                            <h3 className="font-serif text-brand-bark text-[2.2rem] leading-[1.1] mb-3">{s.title}</h3>
                            <p className="text-brand-bark/75 font-light text-[14.5px] leading-[1.7]">{s.desc}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </section>


      {/* WIZARD */}
      <section ref={wizardRef} className="relative bg-brand-cream border-y border-brand-bark/10 py-16 md:py-28" dir="rtl">
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl xl:max-w-[1100px]">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-brand-terracotta font-bold mb-5 md:mb-7">
              Personal Questionnaire
            </p>
            <h2 className="font-serif leading-[0.92] tracking-wide text-[3rem] sm:text-5xl md:text-6xl lg:text-[5.5rem] mb-6 md:mb-8">
              <span className="text-brand-bark">מלאי את </span>
              <span className="text-brand-terracotta italic">השאלון</span>
            </h2>
            <div className="w-12 h-px bg-brand-terracotta mx-auto mb-6 md:mb-8" />
            <p className="text-brand-bark/70 font-light text-[15px] md:text-lg leading-relaxed">
              2-3 דקות, ורהאם תחזור אלייך עם תרכובת מדויקת.
            </p>
          </div>

          {/* Progress */}
          <div className="mb-8 md:mb-10">
            <div className="flex items-center justify-end text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-brand-terracotta mb-3">
              <span className="tabular-nums">{progress}%</span>
            </div>
            <div className="h-[2px] w-full bg-brand-bark/10 overflow-hidden">
              <motion.div
                className="h-full bg-brand-gold"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </div>
          </div>

        <div className="bg-brand-sand border border-brand-bark/10 border-t-4 border-t-brand-gold shadow-[0_30px_90px_-40px_hsl(var(--brand-bark)/0.35)] p-6 sm:p-10 md:p-14 lg:p-16 min-h-[440px] md:min-h-[500px] lg:min-h-[540px] flex flex-col">
          <AnimatePresence mode="wait">
            {currentStep && (
              <motion.div
                key={`step-${stepIndex}`}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col gap-7 lg:gap-8 flex-1"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {sectionInfo && (
                    <span className="inline-flex flex-wrap items-center gap-2 px-3 py-1.5 bg-brand-gold/15 border border-brand-gold/40 text-[#412018] text-[11px] md:text-xs font-medium tracking-wide">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-gold" />
                      {categoryProgress.currentIdx >= 0 && (
                        <span className="uppercase tracking-[0.2em] text-[#67392b] font-bold tabular-nums">
                          תחום {categoryProgress.currentIdx + 1}/
                          {categoryProgress.total}
                        </span>
                      )}
                      <span className="text-[#412018]/40">·</span>
                      {sectionInfo.name}
                      <span className="text-[#412018]/60 tabular-nums">
                        · {sectionInfo.idx + 1}/{sectionInfo.total}
                      </span>
                    </span>
                  )}
                  <span className="text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-[#67392b] font-bold tabular-nums">
                    שלב {String(stepIndex + 1).padStart(2, '0')} מתוך{' '}
                    {String(totalSteps).padStart(2, '0')}
                  </span>
                </div>

                {currentStep.kind === 'single' ? (
                  <>
                    <h2 className="font-serif text-[#412018] leading-[1.1] text-[2rem] sm:text-[2.4rem] md:text-[2.6rem] lg:text-[3rem]">
                      {currentStep.q.label}
                    </h2>
                    {currentStep.q.hint && (
                      <p className="text-[#412018]/65 font-sans font-light text-sm md:text-base leading-relaxed -mt-2">
                        {currentStep.q.hint}
                      </p>
                    )}
                    <div className="flex-1">
                      {currentStep.q.type === 'single' && (
                        <SingleOption
                          value={answers[currentStep.q.id] as string | undefined}
                          options={currentStep.q.options || []}
                          onChange={(v) => setAnswer(currentStep.q.id, v)}
                        />
                      )}
                      {currentStep.q.type === 'multi' && (
                        <MultiOption
                          value={(answers[currentStep.q.id] as string[]) || []}
                          options={currentStep.q.options || []}
                          onChange={(v) => setAnswer(currentStep.q.id, v)}
                        />
                      )}
                      {currentStep.q.type === 'text' && (
                        <textarea
                          value={(answers[currentStep.q.id] as string) || ''}
                          onChange={(e) => setAnswer(currentStep.q.id, e.target.value)}
                          placeholder={currentStep.q.placeholder}
                          maxLength={500}
                          rows={6}
                          className="w-full bg-brand-sand border border-[#412018]/15 px-5 py-4 text-[#412018] placeholder:text-[#412018]/40 focus:outline-none focus:border-[#fbbf25] focus:ring-2 focus:ring-[#fbbf25]/20 font-sans font-light leading-relaxed resize-none text-base md:text-lg"
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="font-serif text-[#412018] leading-[1.1] text-[2rem] sm:text-[2.4rem] md:text-[2.6rem] lg:text-[3rem]">
                      כמה שאלות קצרות של כן / לא
                    </h2>
                    <p className="text-[#412018]/65 font-sans font-light text-sm md:text-base leading-relaxed -mt-2">
                      ענו על כל השאלות במסך הזה — זה יקצר את התהליך.
                    </p>
                    <div className="flex-1 flex flex-col gap-5 md:gap-6">
                      {currentStep.questions.map((q, i) => {
                        const val = answers[q.id] as string | undefined;
                        return (
                          <div
                            key={q.id}
                            className="border-t border-[#412018]/10 pt-5 md:pt-6 first:border-t-0 first:pt-0"
                          >
                            <div className="flex items-start justify-between gap-4 mb-3">
                              <p className="font-sans font-normal text-[#412018] text-[15px] md:text-base lg:text-lg leading-snug flex-1">
                                <span className="text-[#67392b]/60 text-xs md:text-sm tabular-nums me-2">
                                  {String(i + 1).padStart(2, '0')}
                                </span>
                                {q.label}
                              </p>
                            </div>
                            <div className="grid grid-cols-2 gap-3 max-w-md">
                              {(q.options || []).map((opt) => {
                                const selected = val === opt;
                                return (
                                  <button
                                    key={opt}
                                    type="button"
                                    onClick={() => setAnswer(q.id, opt)}
                                    className={`relative text-center px-4 py-3 md:py-3.5 border transition-all duration-300 ${
                                      selected
                                        ? 'border-[#fbbf25] bg-[#fbbf25]/10 text-[#412018]'
                                        : 'border-[#412018]/15 bg-brand-sand hover:border-[#67392b]/40 hover:bg-brand-bark/5 text-[#412018]/80'
                                    }`}
                                  >
                                    <span className="font-sans font-light">{opt}</span>
                                    {selected && (
                                      <Check
                                        size={14}
                                        className="absolute top-1/2 -translate-y-1/2 left-3 text-[#67392b]"
                                      />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}


                <div className="flex items-center justify-between gap-3 pt-2">
                  <button
                    onClick={goBack}
                    className="flex items-center gap-2 text-[11px] md:text-[12px] uppercase tracking-[0.25em] text-[#412018]/60 hover:text-[#412018] transition-colors"
                  >
                    <ArrowRight size={14} />
                    חזרה
                  </button>
                  <button
                    onClick={goNext}
                    className="group relative overflow-hidden bg-[#412018] text-[#fbbf25] px-7 md:px-8 py-3.5 md:py-4"
                  >
                    <span className="absolute inset-0 bg-[#fbbf25] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                    <span className="relative flex items-center gap-3 text-[11px] md:text-[12px] font-bold uppercase tracking-[0.25em] group-hover:text-[#412018] transition-colors duration-500">
                      המשך
                      <ArrowLeft size={14} />
                    </span>
                  </button>
                </div>
              </motion.div>
            )}

            {onContact && (
              <motion.div
                key="contact"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col gap-6 lg:gap-7"
              >
                <div className="flex items-baseline justify-between text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-[#67392b]">
                  <span className="font-bold">פרטי יצירת קשר</span>
                  <span>שלב אחרון</span>
                </div>
                <h2 className="font-serif text-[#412018] leading-[1.1] text-2xl sm:text-3xl md:text-[2.6rem] lg:text-[3rem]">
                  איך נחזור אלייך?
                </h2>
                <p className="text-[#412018]/70 text-sm md:text-base leading-relaxed -mt-2">
                  רהאם תעבור על התשובות ותחזור אלייך אישית עם התאמה והמלצה.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  <div className="sm:col-span-2">
                    <label className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#67392b] block mb-2">
                      שם מלא
                    </label>
                    <input
                      type="text"
                      value={contact.name}
                      onChange={(e) =>
                        setContact({ ...contact, name: e.target.value })
                      }
                      maxLength={100}
                      className="w-full bg-brand-sand border border-[#412018]/15 px-4 py-3 md:py-4 text-base md:text-lg focus:outline-none focus:border-[#fbbf25] focus:ring-2 focus:ring-[#fbbf25]/20"
                    />
                    {errors.name && (
                      <p className="text-red-700 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#67392b] block mb-2">
                      טלפון
                    </label>
                    <input
                      type="tel"
                      dir="ltr"
                      value={contact.phone}
                      onChange={(e) =>
                        setContact({ ...contact, phone: e.target.value })
                      }
                      maxLength={30}
                      className="w-full bg-brand-sand border border-[#412018]/15 px-4 py-3 md:py-4 text-right text-base md:text-lg focus:outline-none focus:border-[#fbbf25] focus:ring-2 focus:ring-[#fbbf25]/20"
                    />
                    {errors.phone && (
                      <p className="text-red-700 text-xs mt-1">{errors.phone}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#67392b] block mb-2">
                      אימייל
                    </label>
                    <input
                      type="email"
                      dir="ltr"
                      value={contact.email}
                      onChange={(e) =>
                        setContact({ ...contact, email: e.target.value })
                      }
                      maxLength={255}
                      className="w-full bg-brand-sand border border-[#412018]/15 px-4 py-3 md:py-4 text-right text-base md:text-lg focus:outline-none focus:border-[#fbbf25] focus:ring-2 focus:ring-[#fbbf25]/20"
                    />
                    {errors.email && (
                      <p className="text-red-700 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#67392b] block mb-2">
                      הערה לרהאם (אופציונלי)
                    </label>
                    <textarea
                      value={contact.note}
                      onChange={(e) =>
                        setContact({ ...contact, note: e.target.value })
                      }
                      rows={4}
                      maxLength={500}
                      className="w-full bg-brand-sand border border-[#412018]/15 px-4 py-3 md:py-4 text-base md:text-lg focus:outline-none focus:border-[#fbbf25] focus:ring-2 focus:ring-[#fbbf25]/20 resize-none"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 pt-2">
                  <button
                    onClick={goBack}
                    className="flex items-center gap-2 text-[11px] md:text-[12px] uppercase tracking-[0.25em] text-[#412018]/60 hover:text-[#412018] transition-colors"
                  >
                    <ArrowRight size={14} />
                    חזרה
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="group relative overflow-hidden bg-[#412018] text-[#fbbf25] px-8 md:px-9 py-4 disabled:opacity-60"
                  >
                    <span className="absolute inset-0 bg-[#fbbf25] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                    <span className="relative flex items-center gap-3 text-[11px] md:text-[12px] font-bold uppercase tracking-[0.28em] group-hover:text-[#412018] transition-colors duration-500">
                      {submitting ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          שולח…
                        </>
                      ) : (
                        <>
                          שליחה לרהאם
                          <ArrowLeft size={14} />
                        </>
                      )}
                    </span>
                  </button>
                </div>
              </motion.div>
            )}

            {onSuccess && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col items-center text-center gap-6 py-10"
              >
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#fbbf25]/15 border border-[#fbbf25] flex items-center justify-center">
                  <Check size={28} className="text-[#67392b]" />
                </div>
                <h2 className="font-serif text-[#412018] leading-[1.05] text-3xl sm:text-4xl md:text-5xl">
                  תודה. <span className="italic text-[#67392b]">קיבלנו את השאלון.</span>
                </h2>
                <p className="text-[#412018]/75 max-w-md md:max-w-lg text-sm md:text-base leading-relaxed">
                  רהאם תעבור על התשובות שלך ותחזור אלייך בקרוב עם המלצה אישית
                  ותרכובת שמתאימה בדיוק לך.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                  <Link
                    to="/"
                    className="px-7 md:px-8 py-3.5 md:py-4 text-[11px] md:text-[12px] font-bold uppercase tracking-[0.25em] bg-[#412018] text-[#fbbf25] hover:bg-[#67392b] transition-colors"
                  >
                    חזרה לעמוד הראשי
                  </Link>
                  <button
                    onClick={reset}
                    className="px-7 md:px-8 py-3.5 md:py-4 text-[11px] md:text-[12px] font-bold uppercase tracking-[0.25em] border border-[#412018]/20 text-[#412018]/80 hover:bg-[#412018] hover:text-[#fbbf25] transition-colors"
                  >
                    מילוי שאלון נוסף
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Reassurance row */}
        {!onSuccess && (
          <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 text-center">
            {[
              { icon: Leaf, title: 'רכיבים טבעיים', text: 'תרכובות מבוססות צמחים, ללא כימיה מיותרת' },
              { icon: HeartHandshake, title: 'ליווי אישי', text: 'רהאם עוברת אישית על כל שאלון' },
              { icon: ShieldCheck, title: 'פרטיות מלאה', text: 'הפרטים נשמרים אצלנו ולא משותפים' },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="flex flex-col items-center gap-2 text-brand-bark/80">
                <Icon size={18} className="text-brand-terracotta" />
                <span className="text-[12px] uppercase tracking-[0.25em] font-bold text-brand-bark">
                  {title}
                </span>
                <p className="text-xs md:text-sm text-brand-bark/65 max-w-[200px] md:max-w-[240px] leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Debug count — visible during dev only when many questions */}
        <div className="sr-only">{allQuestions.length} questions total</div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-16 md:py-28" dir="rtl">
        <div className="container mx-auto px-5 md:px-6 max-w-[1000px] relative">
          <div className="text-center max-w-[820px] mx-auto mb-12 md:mb-16">
            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-5 md:mb-7">
              FAQ
            </p>
            <h2 className="font-serif leading-[0.92] tracking-wide text-[3rem] sm:text-5xl md:text-6xl lg:text-[5.5rem] mb-6 md:mb-8">
              <span className="text-[#412018]">שאלות </span>
              <span className="text-[#67392b] italic">נפוצות</span>
            </h2>
            <div className="w-12 h-px bg-[#67392b] mx-auto mb-6 md:mb-8" />
            <p className="text-[#412018]/70 font-light text-[15px] md:text-lg leading-relaxed">
              כל מה שחשוב לדעת על רוקחות אישית — ואם נשארה שאלה, אפשר תמיד לפנות לרהאם.
            </p>
          </div>

          <div className="border-y border-[#412018]/15">
            {[
              { q: 'מה זאת בעצם רוקחות אישית?', a: 'רהאם מרכיבה לך תרכובת ייעודית — לפי הבעיה, סוג העור או הקרקפת, והמטרות שלך. לא קרם מהמדף, אלא פורמולה שמכוונת בדיוק לצורך שלך.' },
              { q: 'כמה זמן לוקח למלא את השאלון?', a: 'בין שתיים לשלוש דקות. השאלות קצרות, השאלון מסתעף לפי התשובות שלך כדי לא להעמיס.' },
              { q: 'מה קורה אחרי ששלחתי את השאלון?', a: 'רהאם עוברת אישית על התשובות, יוצרת איתך קשר עם הצעה מפורטת, מרכיבה את התרכובת ושולחת אותה אליך.' },
              { q: 'תוך כמה זמן מקבלים תשובה?', a: 'בדרך כלל תוך 24-48 שעות בימי עסקים. בשאלות דחופות אפשר לפנות גם טלפונית.' },
              { q: 'האם המידע שלי נשמר בדיסקרטיות?', a: 'בהחלט. כל המידע נשמר באופן מאובטח ומשמש אך ורק להתאמת התרכובת שלך.' },
              { q: 'האם אפשר לשנות או להתאים את התרכובת בהמשך?', a: 'כן. רהאם מלווה אותך גם אחרי הקבלה — אם משהו צריך כיוון עדין, מתאימים יחד.' },
            ].map((item, i) => (
              <FAQItem key={i} q={item.q} a={item.a} index={i} />
            ))}
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default PersonalRx;
